import { useEffect, useRef, useState } from 'react'

interface EntityDialogProps {
  open: boolean
  title: string
  /** e2e selector / DOM id 용. 부여하면 컨테이너 + input + buttons 에 일관된 id prefix 적용. */
  id?: string
  /** input 의 별도 id (없으면 `${id}-name` 사용) */
  nameInputId?: string
  /** confirm button 의 별도 id (없으면 `btn-create-${id}` 또는 `btn-save-${id}`) */
  confirmButtonId?: string
  nameLabel?: string
  namePlaceholder?: string
  descLabel?: string
  descPlaceholder?: string
  defaultName?: string
  defaultDesc?: string
  confirmText?: string
  onSubmit: (value: { name: string; description: string }) => void
  onCancel: () => void
}

export function EntityDialog({
  open,
  title,
  id,
  nameInputId,
  confirmButtonId,
  nameLabel = 'Name',
  namePlaceholder,
  descLabel = 'Description',
  descPlaceholder = 'Optional description',
  defaultName = '',
  defaultDesc = '',
  confirmText = 'Save',
  onSubmit,
  onCancel,
}: EntityDialogProps) {
  const [name, setName] = useState(defaultName)
  const [desc, setDesc] = useState(defaultDesc)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setName(defaultName)
      setDesc(defaultDesc)
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open, defaultName, defaultDesc])

  if (!open) return null

  const submit = () => {
    const trimmed = name.trim()
    if (!trimmed) return
    onSubmit({ name: trimmed, description: desc.trim() })
  }

  const overlayId = id
  const inputId = nameInputId
  const okId = confirmButtonId

  return (
    <div id={overlayId} className="modal-overlay show active" onClick={onCancel} style={{ display: 'flex' }}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">{title}</div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label" htmlFor={inputId}>{nameLabel}</label>
            <input
              id={inputId}
              ref={inputRef}
              type="text"
              value={name}
              placeholder={namePlaceholder}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); submit() }
                else if (e.key === 'Escape') { e.preventDefault(); onCancel() }
              }}
            />
          </div>
          <div className="form-group">
            <label className="form-label">{descLabel}</label>
            <textarea
              value={desc}
              placeholder={descPlaceholder}
              onChange={(e) => setDesc(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { e.preventDefault(); onCancel() }
              }}
              rows={3}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button id={okId} type="button" className="btn btn-primary" onClick={submit}>{confirmText}</button>
        </div>
      </div>
    </div>
  )
}
